import { useState } from 'react';
import { ConfirmDialog } from '../ui';
import type { ExistingCustomers } from '../../services/resellers.service';

interface DeactivateResellerDialogProps {
  title: string;
  /** What deactivation always does, e.g. "New uses stop and the reseller can't sign in." */
  effect: string;
  currentCustomers: number;
  isLoading: boolean;
  error?: string | null;
  onClose: () => void;
  onConfirm: (choice: ExistingCustomers) => void | Promise<void>;
}

const choices: ReadonlyArray<readonly [ExistingCustomers, string, string]> = [
  [
    'KEEP',
    'Keep it for existing customers',
    'They keep their discount and the reseller keeps earning commission, by their terms.',
  ],
  [
    'REMOVE',
    'Remove it from existing customers',
    'Full price and no commission from their next payment. Commission already earned stays.',
  ],
];

// Reseller or coupon deactivation. Mounted only while open, so every deactivation
// needs a fresh, explicit Keep/Remove choice (the API requires one too).
export const DeactivateResellerDialog = ({
  title,
  effect,
  currentCustomers,
  isLoading,
  error,
  onClose,
  onConfirm,
}: DeactivateResellerDialogProps) => {
  const [choice, setChoice] = useState<ExistingCustomers | null>(null);

  return (
    <ConfirmDialog
      isOpen
      onClose={onClose}
      onConfirm={() => (choice ? onConfirm(choice) : undefined)}
      title={title}
      variant="warning"
      confirmText="Deactivate"
      isLoading={isLoading}
      confirmDisabled={!choice}
      description={
        <div className="space-y-3 text-left">
          <p>
            {effect} {currentCustomers} customer{currentCustomers === 1 ? '' : 's'} currently use
            {currentCustomers === 1 ? 's' : ''} it. Choose what happens to them:
          </p>
          {choices.map(([value, label, detail]) => (
            <label
              key={value}
              className="flex items-start gap-2 p-3 rounded-lg border border-(--color-border-gray) cursor-pointer"
            >
              <input
                type="radio"
                name="existingCustomers"
                value={value}
                checked={choice === value}
                onChange={() => setChoice(value)}
                disabled={isLoading}
                className="mt-1 accent-orange-400"
              />
              <span>
                <span className="block text-white font-medium">{label}</span>
                <span className="block text-xs">{detail}</span>
              </span>
            </label>
          ))}
          {error && (
            <p role="alert" className="text-(--color-error)">
              {error}
            </p>
          )}
        </div>
      }
    />
  );
};
